import { inv } from 'mathjs';
import {
  Circle,
  GoogleMap,
  Marker,
  OverlayView,
  Polyline,
  Polygon,
  useJsApiLoader,
} from "@react-google-maps/api";

import { useTimeFormatReal } from "../../library/useTimeFormat";
import Modal from 'react-modal';
import { memo, useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import { useRecoilState, useRecoilValue } from "recoil";
import { accessTokenAtom, selectedDeviceNameAtom, userInfoAtom } from "../../recoil";

// 일반 마커
import RedMarker from "../../image/redMarker.png";
import BlueMarker from "../../image/blueMarker.png";
import YellowMarker from "../../image/yellowMarker.png";

// 커스텀 마커
import SpanMarker from "../../image/fullCircle.png";
import SbMarker from "../../image/donutCircle.png";
import HomeMarker from "../../image/homeMarker.png";
import PeopleMarker from "../../image/peopleMarker.png";
import BicycleMarker from "../../image/bicycleMarker.png";
import CarMarker from "../../image/carMarker.png";
import DroneMarker from "../../image/droneMarker.png";

import { getLocalStorageWithExpiry } from "../../locales/langFunc";
import getCountry from "../../library/getCountry";
import { checkLocationWithinRadius } from "../../library/calDistance";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PolygonModal from '../Modal/PolygonModal';
import PolygonListModal from '../Modal/PolygonListModal'; // Import PolygonListModalC  
import { FaExpandArrowsAlt, FaRedoAlt, FaCrosshairs, FaSyncAlt, FaTimes, FaCog, FaExchangeAlt,  FaMapMarkerAlt, FaRoute, FaListAlt, FaSearchPlus, FaSearchMinus, FaRulerHorizontal, FaRulerVertical, FaClock, FaAngleDoubleUp } from 'react-icons/fa';
import { useRef } from 'react';
import { Scatter } from 'react-chartjs-2';
import { getError } from '../../library/deviationState';

import { Chart as ChartJS, LinearScale, PointElement, Tooltip, Legend, Title, Filler } from 'chart.js';
import { axios } from '../../apis/utils';
ChartJS.register(LinearScale, PointElement, Tooltip, Legend, Title, Filler);

// 원을 그리기 위한 커스텀 플러그인
const drawCirclesPlugin = {
  id: 'drawCircles',
  afterDraw: (chart) => {
    const ctx = chart.ctx;
    const xCenter = chart.scales.x.getPixelForValue(0);
    const yCenter = chart.scales.y.getPixelForValue(0);
    const radiusStep = chart.scales.x.getPixelForValue(5) - xCenter; // 5cm 거리당 픽셀 수 계산

    ctx.save();
    ctx.strokeStyle = '#007bff'; // 원의 색상
    ctx.lineWidth = 1.5; // 원의 선 두께

    // 그리드에 맞춰 원을 그림
    ctx.beginPath(); // 새 경로 시작
    ctx.arc(xCenter, yCenter, radiusStep * 1, 0, 2 * Math.PI);
    ctx.stroke();
    ctx.restore();
  },
};


const donutMarkerPlugin = {
  id: 'donutMarkerPlugin',
  afterDraw: (chart) => {
    const ctx = chart.ctx;
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      meta.data.forEach((point, index) => {
        if (dataset.pointStyle[index] === 'donut') {
          const x = point.x;
          const y = point.y;
          const outerRadius = point.radius;
          const innerRadius = outerRadius / 2; // Donut hole radius

          ctx.beginPath();
          ctx.arc(x, y, outerRadius, 0, Math.PI * 2);
          ctx.fillStyle = dataset.backgroundColor[index];
          ctx.fill();
          ctx.stroke();

          ctx.beginPath();
          ctx.arc(x, y, innerRadius, 0, Math.PI * 2);
          ctx.fillStyle = '#ffffff'; // White color for the donut hole
          ctx.fill();
        }
      });
    });
  },
};

const createQueue = () => ({
  baseQueue: [],
  compareQueue: [],
});

const filterOldData = (queue, currentBaseTime) => {
  const baseTimestamp = Math.floor(new Date(currentBaseTime).getTime() / 1000); // 기준 시간의 초 단위 변환
  const seenTimestamps = new Set(); // 중복된 createdAt을 제거하기 위한 Set

  return queue.filter((item) => {
    const itemTimestamp = Math.floor(new Date(item.createdAt).getTime() / 1000); // 비교 대상 시간의 초 단위 변환

    if (seenTimestamps.has(itemTimestamp)) {
      // 중복된 createdAt이면 필터링에서 제외
      return false;
    }

    seenTimestamps.add(itemTimestamp); // Set에 해당 타임스탬프 추가

    // 기준 초와 비교 초의 차이가 ±5초 이내인 데이터만 유지
    return Math.abs(baseTimestamp - itemTimestamp) <= 5;
  });
};


const xyz2gd = (x, y, z) => {
  try {
    x = parseFloat(x);
    y = parseFloat(y);
    z = parseFloat(z);

    const a = 6378137.0;
    const f = parseFloat(1 / 298.257223563);
    const b = a * (1 - f);

    const eSQ = (a ** 2 - b ** 2) / a ** 2;

    let lon = Math.atan2(parseFloat(y), parseFloat(x)) * 180 / Math.PI;
    if (lon > 180) lon -= 360;
    if (lon < -180) lon += 360;

    const p = Math.sqrt(x ** 2 + y ** 2);
    let Phi0 = Math.atan2(parseFloat(z) * inv(1 - eSQ), p);

    let q = 0;
    let Phi = 0;
    let h = 0;

    while (q !== 1) {
      let N0 = a ** 2 / Math.sqrt(a ** 2 * Math.cos(Phi0) ** 2 + b ** 2 * Math.sin(Phi0) ** 2);
      h = p / Math.cos(Phi0) - N0;
      Phi = Math.atan2(parseFloat(z) * inv(1 - eSQ * (N0 / (N0 + h))), p);

      if (Math.abs(Phi - Phi0) <= 1e-13) break;
      Phi0 = Phi;
    }

    const lat = Phi * 180 / Math.PI;
    return [lat, lon, h];
  } catch (error) {
    return [0, 0, 0];
  }
};

const gd2xyz = (lat, lon, h) => {
  try {
    const a = 6378137.0;
    const f = 1 / 298.257223563;
    const b = a * (1 - f);

    const lat_rad = lat * Math.PI / 180;
    const lon_rad = lon * Math.PI / 180;

    const N = a ** 2 / Math.sqrt(a ** 2 * Math.cos(lat_rad) ** 2 + b ** 2 * Math.sin(lat_rad) ** 2);
    const eSQ = (a ** 2 - b ** 2) / a ** 2;

    const X = (N + h) * Math.cos(lat_rad) * Math.cos(lon_rad);
    const Y = (N + h) * Math.cos(lat_rad) * Math.sin(lon_rad);
    const Z = (N * (1 - eSQ) + h) * Math.sin(lat_rad);

    return [X, Y, Z];
  } catch (error) {
    return [0, 0, 0];
  }
};

const xyz2topo = (x, y, z, lat, lon) => {
  const lat_rad = lat * Math.PI / 180;
  const lon_rad = lon * Math.PI / 180;

  const sin_lat = Math.sin(lat_rad);
  const cos_lat = Math.cos(lat_rad);
  const sin_lon = Math.sin(lon_rad);
  const cos_lon = Math.cos(lon_rad);

  const north = -sin_lat * cos_lon * x - sin_lat * sin_lon * y + cos_lat * z;
  const east = -sin_lon * x + cos_lon * y;
  const up = cos_lat * cos_lon * x + cos_lat * sin_lon * y + sin_lat * z;

  return [north, east, up];
};

const getHV = (lat, lon, h, tx, ty, tz) => {
  const xyz = gd2xyz(lat, lon, h);

  const xe = xyz[0] - tx;
  const ye = xyz[1] - ty;
  const ze = xyz[2] - tz;

  const truePos = xyz2gd(tx, ty, tz);

  const [north, east, up] = xyz2topo(xe, ye, ze, truePos[0], truePos[1]);

  const H = Math.sqrt(north ** 2 + east ** 2);

  const V = up;

  return { H, V };
};

const showToast = (message) => {
  toast(message, {
    autoClose: false,
  });
};

const polylineOptions = {
  strokeColor: "#5e95d8",
  strokeOpacity: 1,
  strokeWeight: 5,
  clickable: false,
  zIndex: 0,
};

const lang = getLocalStorageWithExpiry("i18nextLng") || getCountry();

function KeywordExplanationModal({ isOpen, onClose }) {
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Keyword Explanation"
      style={{
        overlay: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
        zIndex: 10000,
        content: {
          borderRadius: '10px',
          maxWidth: '600px',
          margin: '0 auto',
        },
      }}
    >
      <KeywordModalContent>
        <CloseButton onClick={onClose}>✕</CloseButton>
        <KeywordTitle>Marker Keyword Guide</KeywordTitle>

        <MarkerExplanation>
          <KeywordLabel>span:</KeywordLabel>
          <MarkerImage src={SpanMarker} alt="Span Marker" />
        </MarkerExplanation>

        <MarkerExplanation>
          <KeywordLabel>sb / javad / septentrio:</KeywordLabel>
          <MarkerImage src={SbMarker} alt="Sb Marker" />
        </MarkerExplanation>

        <MarkerExplanation>
          <KeywordLabel>home / static / station:</KeywordLabel>
          <MarkerImage src={HomeMarker} alt="Home Marker" />
        </MarkerExplanation>

        <MarkerExplanation>
          <KeywordLabel>people / man / woman:</KeywordLabel>
          <MarkerImage src={PeopleMarker} alt="People Marker" />
        </MarkerExplanation>

        <MarkerExplanation>
          <KeywordLabel>car / vehicle:</KeywordLabel>
          <MarkerImage src={CarMarker} alt="Car Marker" />
        </MarkerExplanation>

        <MarkerExplanation>
          <KeywordLabel>drone:</KeywordLabel>
          <MarkerImage src={DroneMarker} alt="Drone Marker" />
        </MarkerExplanation>

        <MarkerExplanation>
          <KeywordLabel>bicycle:</KeywordLabel>
          <MarkerImage src={BicycleMarker} alt="Bicycle Marker" />
        </MarkerExplanation>

        {/* Default marker explanation */}
        <MarkerExplanation>
          <KeywordLabel>Default(No Keyword):</KeywordLabel>
          <MarkerSet>
            <MarkerDescriptionWrapper>
              <MarkerImage src={BlueMarker} alt="Blue Marker" />
              <MarkerInfo>Fix</MarkerInfo>
            </MarkerDescriptionWrapper>
            <MarkerDescriptionWrapper>
              <MarkerImage src={YellowMarker} alt="Yellow Marker" />
              <MarkerInfo>Float</MarkerInfo>
            </MarkerDescriptionWrapper>
            <MarkerDescriptionWrapper>
              <MarkerImage src={RedMarker} alt="Red Marker" />
              <MarkerInfo>Other</MarkerInfo>
            </MarkerDescriptionWrapper>
          </MarkerSet>
        </MarkerExplanation>

        {/* Explanatory Text */}
        <ExplanationText>
          이름에 해당 키워드가 포함되면 마커이미지가 변경됩니다.
        </ExplanationText>
      </KeywordModalContent>
    </Modal>
  );
}

const MonitoringMap = ({ socketData, isGeoShow, isGeoAlert }) => {
  const [center, setCenter] = useState({ lat: 37.275086629781896, lng: 127.05535816620868 });
  const [geoObject, setGeoObject] = useState([]);
  const [selectedDevice, setSelectedDevice] = useRecoilState(selectedDeviceNameAtom);
  const accessToken = useRecoilValue(accessTokenAtom);
  const userInfo = useRecoilValue(userInfoAtom);
  const [polygonCoordsDB, setPolygonCoordsDB] = useState([]);
  const [isPolygonModalOpen, setPolygonModalOpen] = useState(false);
  const [isPolygonListModalOpen, setPolygonListModalOpen] = useState(false);

  /* 변위값 계산식 시작 */
  const [baseSerial, setBaseSerial] = useState(""); // 기준 serial
  const [compareSerial, setCompareSerial] = useState(""); // 비교 serial
  const [displacement, setDisplacement] = useState(null); // 변위값 (H 호라이즌, V 버티컬)
  const [deviationPoints, setDeviationPoints] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [scatterZoomLevel, setScatterZoomLevel] = useState(2); // For scatter ~ zoom max 1000
  const [isComparisonActive, setComparisonActive] = useState(false);
  const [isButtonComparisonActive, setComparisonButtonActive] = useState(false);
  const [dragging, setDragging] = useState(false);

  const [coordinates, setCoordinates] = useState([{ lat: '', lng: '', radius: '' }]);
  const [mouseCoordinates, setMouseCoordinates] = useState({ lat: '', lng: ''});
  const [mouseCoordinates2, setMouseCoordinates2] = useState({ lat: '', lng: ''});
  const [isModalTemporarilyClosed, setModalTemporarilyClosed] = useState(false);
  const [coordinateIndex, setCoordinateIndex] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if(userInfo?.email) {
      const updatePath = async () => {
        try {
          await axios.post(`/teamPathUpdate`, {
            email: userInfo?.email,
            path: JSON.stringify(polygonCoordsDB),
          });
        } catch (error) {
          console.error('Error updating path:', error);
        }
      };
      updatePath();
      setPolygonCoordsDB(polygonCoordsDB);
    }
  }, [polygonCoordsDB, userInfo]);

  useEffect(() => {
    if (userInfo?.email) {
      const fetchPolygonPath = async () => {
        try {
          const response = await axios.get(`/teamPathGet?email=${userInfo.email}`);
          if (response.data) {
            const storedPath = JSON.parse(response.data);
            setPolygonCoordsDB(storedPath);
            console.log(storedPath);
          }
        } catch (error) {
          console.error("Failed to fetch polygon path:", error);
        }
      };
      fetchPolygonPath();
    }
  }, [userInfo]);



  const handleMapClick = (e) => {
    if (isModalTemporarilyClosed) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();

      setCoordinates((prevCoords) => {
        const newCoords = [...prevCoords];
        newCoords[coordinateIndex] = { lat, lng };
        return newCoords;
      });

      if(isPolygonModalOpen) {
        setMouseCoordinates({ lat, lng });
      }
      
      if(isPolygonListModalOpen) {
        setMouseCoordinates2({ lat, lng });
      }

      setModalTemporarilyClosed({ lat: lat, lng: lng});
    }
  };

  const handleMapMouseMove = (e) => {
    if (isModalTemporarilyClosed) {
      setMousePosition({ x: e.clientX, y: e.clientY });
    }
  };

  const handleGetLocation = (index) => {
    setModalTemporarilyClosed(true);
  };

  const handleOffGetLocation = () => {
    setModalTemporarilyClosed(false);
  };

  const data = useMemo(() => {
    const seenSerials = new Set();
    return socketData.filter((item) => {
      if (seenSerials.has(item.serial)) {
        return false;
      }
      seenSerials.add(item.serial);
      return true;
    });
  }, [socketData]);

  const handleScatterZoomIn = () => {
    setScatterZoomLevel((prev) => {
      if (prev >= 1000) {
        return prev + 250;
      } else if (prev >= 500) {
        return prev + 100; // 500cm 이상일 때는 100cm씩 확대
      } else if (prev >= 100) {
        return prev + 50; // 100cm ~ 500cm에서는 50cm씩 확대
      } else if (prev < 10) {
        return 10; // 10cm 미만일 때는 10으로 설정
      } else {
        return prev + 10; // 100cm 이하일 때는 10cm씩 확대
      }
    });
  };

  const handleScatterZoomOut = () => {
    setScatterZoomLevel((prev) => {
      if (prev > 1000) {
        return prev - 250; // 500cm 이상일 때는 100cm씩 축소
      } else if (prev > 500) {
        return prev - 100; // 500cm 이상일 때는 100cm씩 축소
      } else if (prev > 100) {
        return prev - 50; // 100cm ~ 500cm에서는 50cm씩 축소
      } else if (prev <= 10) {
        return 2;
      } else {
        return prev - 10; // 100cm 이하일 때는 10cm씩 축소
      }
    });
  };

  const getGridStepSize = (zoomLevel) => {
    if (zoomLevel <= 2) {
      return zoomLevel / 2; // 2x2 grid
    } else if (zoomLevel <= 10) {
      return zoomLevel / 2; // 4x4 grid
    } else if (zoomLevel <= 50) {
      return zoomLevel / 2; // 4x4 grid
    } else if (zoomLevel >= 100) {
      return zoomLevel / 3; // 6x6 grid
    } else {
      return zoomLevel / 3; // Default case, 8x8 grid
    }
  };

  const { date } = useTimeFormatReal();
  const [queue, setQueue] = useState(createQueue()); // base와 compare를 각각 큐로 저장


 // 데이터 업데이트 시마다 큐에 추가
 const [processedTimestamps, setProcessedTimestamps] = useState(new Set());

 useEffect(() => {
  if (!isButtonComparisonActive || !isComparisonActive || data.length === 0 || !baseSerial || !compareSerial) return;

  const cntBaseData = data.filter((item) => item.serial === baseSerial);
  const cntCompareData = data.filter((item) => item.serial === compareSerial);

  const isDataInvalid = (item) => {
    return (
      isNaN(item.lat) || item.lat === null || item.lat === undefined || item.lat === 0 ||
      isNaN(item.lon) || item.lon === null || item.lon === undefined || item.lon === 0 ||
      isNaN(item.alt) || item.alt === null || item.alt === undefined || item.alt === 0 ||
      isNaN(item.geoidal) || item.geoidal === null || item.geoidal === undefined || item.geoidal === 0 ||
      isNaN(new Date(item.createdAt).getTime()) || item.createdAt === null || item.createdAt === undefined ||
      item.quality === null || item.quality === undefined || item.quality === 0
    );
  };

  if (cntBaseData.length === 0 || cntCompareData.length === 0 || cntBaseData.some(isDataInvalid) || cntCompareData.some(isDataInvalid)) {
    return;
  }

  const baseTimestamp = new Date(cntBaseData[0]?.createdAt).getTime();

  if (processedTimestamps.has(Math.floor(baseTimestamp / 1000))) {
    return;
  }

  setQueue((prevQueue) => {
    const updatedBaseQueue = filterOldData([...prevQueue.baseQueue, ...cntBaseData], cntBaseData[0]?.createdAt);
    const updatedCompareQueue = filterOldData([...prevQueue.compareQueue, ...cntCompareData], cntBaseData[0]?.createdAt);
    return {
      baseQueue: updatedBaseQueue,
      compareQueue: updatedCompareQueue,
    };
  });

  calculateDisplacement();
  setProcessedTimestamps((prev) => new Set(prev).add(Math.floor(baseTimestamp / 1000)));
}, [data, baseSerial, compareSerial, isComparisonActive, isButtonComparisonActive]);


// baseSerial과 compareSerial의 데이터를 비교하는 함수
const calculateDisplacement = () => {
  const { baseQueue, compareQueue } = queue;
  if (baseQueue.length === 0 || compareQueue.length === 0) {
    setErrorMessage("기준 또는 비교 데이터가 없습니다.");
    return;
  }

  const latestBaseItem = baseQueue[baseQueue.length - 1]; // 가장 최근 baseSerial 데이터 (마지막 데이터)
  const baseTimestamp = new Date(latestBaseItem.createdAt).getTime();

  // baseSerial의 데이터에 대해 compareSerial의 데이터를 비교
  const closestCompareItem = compareQueue.reduce((closest, compareItem) => {
    const compareTimestamp = new Date(compareItem.createdAt).getTime();
    const timeDifference = Math.abs(compareTimestamp - baseTimestamp);

    if (timeDifference <= 5000 && (!closest || timeDifference < Math.abs(new Date(closest.createdAt).getTime() - baseTimestamp))) {
      return compareItem;
    }
    return closest;
  }, null);

  if (!closestCompareItem) {
    setErrorMessage("기준 초 ±5초 내에 일치하는 비교 데이터를 찾을 수 없습니다.");
    return;
  }

  const sameSecondBaseItem = baseQueue.find(
    (item) => item.createdAt === closestCompareItem.createdAt
  );
  const currentBaseItem = sameSecondBaseItem || null;

  if(currentBaseItem !== null) {

    // 기준 시리얼 x, y, z
    const [x, y, z] = gd2xyz(
      parseFloat(currentBaseItem.lat),
      parseFloat(currentBaseItem.lon),
      parseFloat(currentBaseItem.alt) + parseFloat(currentBaseItem.geoidal)
    );

    // 비교 시리얼 x, y, z
    const [tx, ty, tz] = gd2xyz(
      parseFloat(closestCompareItem.lat),
      parseFloat(closestCompareItem.lon),
      (parseFloat(closestCompareItem.alt) + parseFloat(closestCompareItem.geoidal))
    );

    // 수평, 수직 rms
    const { H, V } = getHV(
      parseFloat(currentBaseItem.lat),
      parseFloat(currentBaseItem.lon),
      (parseFloat(currentBaseItem.alt) + parseFloat(currentBaseItem.geoidal)),
      tx, ty, tz
    );

    const deviationValues = getError(
      parseFloat(closestCompareItem.lat), parseFloat(closestCompareItem.lon), (parseFloat(closestCompareItem.alt) + parseFloat(closestCompareItem.geoidal)),
      x, y, z
    );

    const newPoint = {
      x: ((parseFloat(deviationValues[1])) * 100).toFixed(3),
      y:  ((parseFloat(deviationValues[0])) * 100).toFixed(3),
      bQ: currentBaseItem?.quality,
      cQ: closestCompareItem?.quality,
      t: currentBaseItem ? new Date(currentBaseItem.createdAt).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }) : '',
    };

    setDeviationPoints((prev) => [...prev, newPoint]);
    setDisplacement({
      createdAt: latestBaseItem.createdAt,
      H,
      V,
    });

    setErrorMessage(null);
  } else {
    setErrorMessage("5초 범위내에 일치하는 비교 데이터를 찾을 수 없습니다.");
    return;
  }
};

const handleCompare = () => {
  if (!baseSerial || !compareSerial) {
    setErrorMessage("기준 Serial 또는 비교 Serial을 선택하세요.");
    return;
  }

  if (baseSerial === compareSerial) {
    setErrorMessage("서로 다른 장비를 선택하세요.");
    return;
  }

  setComparisonButtonActive(true);
  calculateDisplacement();
};


const handleToggleComparison = () => {
  if (!dragging) {
    setComparisonActive((prev) => !prev);
  }
  setComparisonButtonActive(false);
};

const scatterData = {
  datasets: [
    {
      label: 'Deviation Points',
      data: deviationPoints.map((point) => ({
        x: parseFloat(point.x),
        y: parseFloat(point.y),
        bQ: point?.bQ || "N/A",
        cQ: point?.cQ || "N/A",
        t: point?.t || "N/A",
      })),
      borderColor: 'rgba(0, 123, 255, 0.6)',
      backgroundColor: deviationPoints.map((point, index) => {
        if (index === deviationPoints.length - 1) {
          return 'rgba(0, 0, 255, 0.9)'; // Dark blue for the current point
        } else if (point.bQ === 'fix' && point.cQ === 'fix') {
          return 'rgba(0, 255, 0, 1.0)'; // Bright green color for fix points
        } else if (index === deviationPoints.length - 1) {
          return 'rgba(0, 0, 255, 0.8)'; // Blue for the most recent point
        } else {
          return 'rgba(255, 0, 0, 0.4)'; // Desaturated red for other points
        }
        
      }),
      showLine: false,
      pointStyle: deviationPoints.map((_, index) => {
        if (index === deviationPoints.length - 1) {
          return 'circle'; // Filled circle for the current position
        } else {
          return 'donut'; // Custom marker for cumulative values
        }
      }),
      pointRadius: deviationPoints.map((point, index) =>
        index === deviationPoints.length - 1 ? 9 : 5 // Set a larger radius for the current point
      ),
    },
  ],
};

// Chart.js options for scatter chart
const scatterOptions = {
  scales: {
    x: {
      type: 'linear',
      position: 'bottom',
      min: -scatterZoomLevel,
      max: scatterZoomLevel,
      title: {
        display: true,
        text: `Δ E [ cm ]`,
        font: {
          size: 16, // Increase font size for grid axis
        },
      },
      grid: {
        color: (context) => (context.tick.value === 0 ? '#dcdcdc' : '#f0f0f0'),
        lineWidth: (context) => (context.tick.value === 0 ? 1.5 : 1),
        stepSize: getGridStepSize(scatterZoomLevel), // Adjust grid step size based on zoom level
      },
      ticks: {
        stepSize: getGridStepSize(scatterZoomLevel),
        callback: function (value) {
          // zoomLevel이 2 이하일 경우 소수점 표시, 그 외엔 정수로 표시
          return scatterZoomLevel >= 2 ? `${Math.round(value)} cm` : `${value.toFixed(1)} cm`;
        },
        font: {
          size: 18,
          weight: 'bold',
        },
      },
    },
    y: {
      min: -scatterZoomLevel,
      max: scatterZoomLevel,
      title: {
        display: true,
        text: `Δ N [ cm ]`,
        font: {
          size: 16, // Increase font size for grid axis
        },
      },
      grid: {
        color: (context) => (context.tick.value === 0 ? '#dcdcdc' : '#f0f0f0'),
        lineWidth: (context) => (context.tick.value === 0 ? 1.5 : 1),
        stepSize: getGridStepSize(scatterZoomLevel),
      },
      ticks: {
        stepSize: getGridStepSize(scatterZoomLevel),
        callback: function (value) {
          // zoomLevel이 2 이하일 경우 소수점 표시, 그 외엔 정수로 표시
          return scatterZoomLevel >= 2 ? `${Math.round(value)} cm` : `${value.toFixed(1)} cm`;
        },
        font: {
          size: 18,
          weight: 'bold',
        },
      },
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const { x, y, bQ, cQ, t } = context.raw;
          return `x: ${x.toFixed(2)} cm, y: ${y.toFixed(2)} cm, bQ: ${bQ}, cQ: ${cQ}, t: ${t}`;
        },
      },
    },
    title: {
      display: true,
      font: {
        size: 24,
        weight: 'bold',
      },
    },
    drawCirclesPlugin: true,
    donutMarkerPlugin: true,
  },
  maintainAspectRatio: false,
  animation: false,
};

const comparisonPanelRef = useRef(null);
useEffect(() => {
  const panel = comparisonPanelRef.current;

  if (!panel) return;

  let isDragging = false;
  let offset = { x: 0, y: 0 };
  let startPosition = { x: 0, y: 0 }; // Track the initial mouse down position

  const handleMouseDown = (e) => {
    startPosition = {
      x: e.clientX,
      y: e.clientY,
    };
    
    offset = {
      x: e.clientX - panel.getBoundingClientRect().left,
      y: e.clientY - panel.getBoundingClientRect().top,
    };
    
    // Add mouse move and mouse up listeners after mousedown
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e) => {
    // Calculate the movement distance
    const distanceX = Math.abs(e.clientX - startPosition.x);
    const distanceY = Math.abs(e.clientY - startPosition.y);

    // If the mouse moved more than a small threshold, activate dragging
    if (distanceX > 5 || distanceY > 5) {
      isDragging = true;
      setDragging(true); // Set dragging to true
      panel.style.left = `${e.clientX - offset.x}px`;
      panel.style.top = `${e.clientY - offset.y}px`;
      panel.style.transition = 'none'; // Disable transition during drag
    }
  };

  const handleMouseUp = () => {
    if (isDragging) {
      setTimeout(() => {
        isDragging = false; // Stop dragging
        setDragging(false); // Reset dragging state
      }, 100);

      panel.style.transition = 'all 0.3s ease-in-out';
    }
    
    // Remove the event listeners after mouse up
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  // Attach the mousedown listener to the panel
  panel.addEventListener('mousedown', handleMouseDown);

  return () => {
    if (panel) {
      panel.removeEventListener('mousedown', handleMouseDown);
    }
  };
}, [comparisonPanelRef.current]);


/* 변위값 계산식 종료 */
  const handleRefresh = () => {
    window.location.reload();
  };

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyB9QJIjB0qhLHWbR68rQw6-JyEtKZG9aqo",
    language: lang,
  });
  const [polylinePath, setPolylinePath] = useState([]);

  const [isKeywordModalOpen, setKeywordModalOpen] = useState(false);

  const handleOpenKeywordModal = () => {
    setKeywordModalOpen(true);
  };

  const handleCloseKeywordModal = () => {
    setKeywordModalOpen(false);
  };



  useEffect(() => {
    if (data.length > 0 && center.lat === 0 && center.lng === 0) {
      setCenter({ lat: parseFloat(data[0].lat) || 0, lng: parseFloat(data[0].lon) || 0 });
    }

    if (selectedDevice !== null) {
      const selectedDeviceInfo = data?.filter(
        (device) => device.serial === selectedDevice
      );

      setCenter({
        lat: parseFloat(selectedDeviceInfo[0]?.lat) || 0,
        lng: parseFloat(selectedDeviceInfo[0]?.lon) || 0,
      });

      setPolylinePath((prevPath) => {
        return [
          ...prevPath,
          { lat: parseFloat(selectedDeviceInfo[0]?.lat) || 0, lng: parseFloat(selectedDeviceInfo[0]?.lon) || 0 },
        ];
      });
    }
    if (isGeoAlert) {
      data.forEach((device) => {
        const { serial } = device;
        const geoDevice = geoObject.find((obj) => obj.serial === serial);

        if (geoDevice && geoDevice.geo) {
          const geoParsed = JSON.parse(geoDevice.geo);

          geoParsed.forEach((geo, index) => {
            const message = checkLocationWithinRadius(
              parseFloat(device.lat) || 0,
              parseFloat(device.lon) || 0,
              geo.lat || 0,
              geo.lon || 0,
              geo.range,
              index,
              serial
            );
            if (message) {
              showToast(message);
            }
          });
        }
      });
    } else {
      toast.dismiss();
    }
  }, [data, center, geoObject, isGeoAlert, selectedDevice]);

  useEffect(() => {
    setPolylinePath([]);
  }, [data, accessToken]);

  const getMarkerIconUrl = (quality, alias) => {
    const lowerAlias = alias ? alias.toLowerCase() : "";
    
    if (lowerAlias.includes("span")) {
      return { url: SpanMarker, size: { width: 25, height: 25 } };
    } else if (lowerAlias.includes("sb") || lowerAlias.includes("javad") || lowerAlias.includes("septentrio")) {
      // Scale the SbMarker to 1.5x its original size
      return { url: SbMarker, size: { width: 30, height: 30 } }; // Adjust size based on your image dimensions
    } else if (lowerAlias.includes("home") || lowerAlias.includes("static") || lowerAlias.includes("station")) {
      return { url: HomeMarker, size: { width: 40, height: 40 } };
    } else if (lowerAlias.includes("people") || lowerAlias.includes("man") || lowerAlias.includes("woman")) {
      return { url: PeopleMarker, size: { width: 40, height: 40 } };
    } else if (lowerAlias.includes("car") || lowerAlias.includes("vehicle")) {
      return { url: CarMarker, size: { width: 40, height: 40 } };
    } else if (lowerAlias.includes("drone")) {
      return { url: DroneMarker, size: { width: 40, height: 40 } };
    } else if (lowerAlias.includes("bicycle")) {
      return { url: BicycleMarker, size: { width: 40, height: 40 } };
    } else {
      if (quality === "fix") {
        return { url: BlueMarker, size: { width: 40, height: 40 } };
      } else if (quality === "float") {
        return { url: YellowMarker, size: { width: 40, height: 40 } };
      } else {
        return { url: RedMarker, size: { width: 40, height: 40 } };
      }
    }
  };
  
  

  const handlePolygonSave = (options) => {
    setPolygonCoordsDB((prevCoords) => [
      ...prevCoords,
      { coords: options.coords, options: { ...options }, type: options.type },
    ]);
    setPolygonModalOpen(false);
  };

  const handleOpenPolygonModal = () => {
    setPolygonModalOpen(true);
  };

  const handleClosePolygonModal = () => {
    setPolygonModalOpen(false);
  };

  const handleOpenPolygonListModal = () => {
    setPolygonListModalOpen(true);
  };

  const handleClosePolygonListModal = () => {
    setPolygonListModalOpen(false);
  };

  const [mapInstance, setMapInstance] = useState(null);
  const [zoom, setZoom] = useState(18);

  const handleZoomChanged = () => {
    if (mapInstance) {
      const currentZoom = mapInstance.getZoom();
      setZoom(currentZoom);
      setSelectedDevice(null);
    }
  };

  const handleOnLoad = (map) => {
    setMapInstance(map);
  };

  return (
    <div onMouseMove={handleMapMouseMove}>
      {isLoaded && (
        <>
          <GoogleMap
            mapContainerStyle={{
              width: "100%",
              height: "500px",
              borderRadius: "12px",
              boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
              marginTop: "5px",
            }}
            options={{
              styles: [
                {
                  featureType: "poi",
                  elementType: "labels",
                  stylers: [{ visibility: "off" }],
                },
              ],
            }}
            center={center}
            zoom={zoom}
            onLoad={handleOnLoad}
            onZoomChanged={handleZoomChanged}
            onClick={handleMapClick}
          >
            {data.map((item) => (
              <div key={item.serial}>
                <Marker
                  icon={{
                    url: getMarkerIconUrl(item.quality, item.alias).url,
                    scaledSize: new window.google.maps.Size(
                      getMarkerIconUrl(item.quality, item.alias).size.width,
                      getMarkerIconUrl(item.quality, item.alias).size.height
                    )
                  }}
                  position={{ lat: parseFloat(item.lat) || 0, lng: parseFloat(item.lon) || 0 }}
                />

                <OverlayView
                  position={{ lat: parseFloat(item.lat) || 0, lng: parseFloat(item.lon) || 0 }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <MarkerTitle isActive={selectedDevice === item.serial}>
                    {item.alias || item.name || " - "}
                  </MarkerTitle>
                </OverlayView>
                <Polyline options={polylineOptions} path={polylinePath} />
              </div>
            ))}

            {data !== null &&
              isGeoShow === true &&
              // eslint-disable-next-line array-callback-return
              geoObject?.map((item, index) => {
                if (item.geo) {
                  const parsedGeo = JSON.parse(item.geo);
                  const serial = item.alias || item.name || " - ";
                  if (parsedGeo && Array.isArray(parsedGeo)) {
                    return parsedGeo.map((geoItem, geoIndex) => (
                      <div key={`${index}-${geoIndex}`}>
                        <Marker
                          position={{
                            lat: parseFloat(geoItem.lat) || 0,
                            lng: parseFloat(geoItem.lon) || 0,
                          }}
                          label={`${parseFloat(geoIndex) + 1}`}
                        />
                        <Circle
                          options={{
                            strokeColor: "#FF0000",
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                            fillColor: "#FF0000",
                            fillOpacity: 0.35,
                          }}
                          center={{
                            lat: parseFloat(geoItem.lat) || 0,
                            lng: parseFloat(geoItem.lon) || 0,
                          }}
                          label={`${parseFloat(geoIndex) + 1}`}
                          radius={parseFloat(geoItem.range)}
                        />
                        <OverlayView
                          position={{
                            lat: parseFloat(geoItem.lat) || 0,
                            lng: parseFloat(geoItem.lon) || 0,
                          }}
                          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        >
                          <MarkerTitle>{`${serial} | ${parseFloat(
                            geoItem.range
                          )}m`}</MarkerTitle>
                        </OverlayView>
                        <Polyline
                          path={[
                            {
                              lat: parseFloat(geoItem.lat) || 0,
                              lng: parseFloat(geoItem.lon) || 0,
                            },
                          ]}
                          options={{ strokeColor: "#FF0000" }}
                        />
                      </div>
                    ));
                  } else {
                    return null;
                  }
                }
              })}
              {/* <Polyline path={polylinePath} options={{ strokeColor: '#5e95d8', strokeOpacity: 1, strokeWeight: 5 }} /> */}
              {/* PloygonLines On Map */}
              {polygonCoordsDB.length > 0 &&
                polygonCoordsDB.map((shape, index) => (
                <div key={index}>
                  {shape.type === 'polygon' ? (
                    <Polygon
                      path={shape.coords}
                      options={{
                        strokeColor: shape.options.strokeColor,
                        strokeOpacity: shape.options.strokeOpacity,
                        strokeWeight: shape.options.strokeWeight,
                        fillColor: shape.options.fillColor,
                        fillOpacity: shape.options.isFilled ? 0.35 : 0,
                      }}
                    />
                  ) : (
                    <Polyline
                      path={shape.coords}
                      options={{
                        strokeColor: shape.options.strokeColor,
                        strokeOpacity: shape.options.strokeOpacity,
                        strokeWeight: shape.options.strokeWeight,
                      }}
                    />
                  )}
                  
                  {shape.options.radiusEnabled && shape.coords.map((point, i) => (
                    point.radius && (
                      <div key={`${index}-${i}`}>
                        <Circle
                          center={{ lat: parseFloat(point.lat) || 0, lng: parseFloat(point.lng) || 0 }}
                          radius={point.radius}
                          options={{
                            strokeColor: shape.options.circleStrokeColor || "#FF0000",
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                            fillColor: shape.options.circleFillColor || "#FF0000",
                            fillOpacity: 0.1,
                          }}
                        />
                        <OverlayView
                          position={{ lat: parseFloat(point.lat) || 0, lng: parseFloat(point.lng) || 0 }}
                          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        >
                          <RadiusLabel zoom={zoom}>
                            {`${point.radius}m`}
                          </RadiusLabel>
                        </OverlayView>
                      </div>
                    )
                  ))}
                </div>
              ))}
          </GoogleMap>
          {isModalTemporarilyClosed && (
            <CursorLabel style={{ top: mousePosition.y + 15, left: mousePosition.x + 15 }}>
              지도위에 마우스커서를 클릭하면 좌표가 설정됩니다.
            </CursorLabel>
          )}
          <ComparisonPanel ref={comparisonPanelRef} className="comparison-panel" isActive={isComparisonActive}>
            <ComparisonHeader>
              <ComparisonToggleButton onClick={handleToggleComparison}>
                <FaExpandArrowsAlt />
                {isComparisonActive ? "Deviation" : "Deviation"}
              </ComparisonToggleButton>
              {isComparisonActive && (
                <MinimizeButton onClick={() => setComparisonActive(false)}>
                  <FaAngleDoubleUp /> {/* Icon for minimize */}
                </MinimizeButton>
              )}
            </ComparisonHeader>

            {isComparisonActive && (
              <ComparisonSettings>
                <label>
                  <FaCrosshairs style={{ marginRight: '5px', fontSize: '20px' }} /> {/* Crosshairs icon for "기준 Serial 선택" */}
                  <select value={baseSerial} onChange={(e) => setBaseSerial(e.target.value)}>
                    <option value="">기준 Serial 선택</option>
                    {data.map((item) => (
                      <option key={item.serial} value={item.serial}>
                        {item.alias || item.name || item.serial}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  <FaExchangeAlt style={{ marginRight: '5px', fontSize: '20px' }} /> {/* Exchange icon for "비교 Serial 선택" */}
                  <select value={compareSerial} onChange={(e) => setCompareSerial(e.target.value)}>
                    <option value="">비교 Serial 선택</option>
                    {data.map((item) => (
                      <option key={item.serial} value={item.serial}>
                        {item.alias || item.name || item.serial}
                      </option>
                    ))}
                  </select>
                </label>

                <ButtonWrapperDisplacementInfo>
                  <ComparisonButton primary onClick={handleCompare}>
                    <FaCog style={{ marginRight: '5px', fontSize: '18px' }} /> {/* Settings icon */}
                    설정
                  </ComparisonButton>

                  <ComparisonButton onClick={() => {
                      setComparisonActive(false);
                      setComparisonButtonActive(false);
                    }}
                  >
                    <FaTimes style={{ marginRight: '5px', fontSize: '18px' }} /> {/* Cancel icon */}
                    취소
                  </ComparisonButton>

                  <ComparisonButton 
                    onClick={() => {
                      setDeviationPoints([]);  // deviation point 초기화
                    }} 
                  >
                    <FaRedoAlt style={{ marginRight: '5px', fontSize: '18px' }} /> {/* 초기화 아이콘 */}
                    초기화
                  </ComparisonButton>

                </ButtonWrapperDisplacementInfo>
                {errorMessage && <WarningMessage>{errorMessage}</WarningMessage>}
              </ComparisonSettings>
            )}
            {displacement && isComparisonActive && (
              <DisplacementInfo isActive={isComparisonActive}>
                <ZoomContainer>
                  <ZoomButton onClick={handleScatterZoomIn} disabled={scatterZoomLevel >= 5000}>
                    <FaSearchMinus size={18} /> {/* Increased icon size */}
                  </ZoomButton>
                  <ZoomButton onClick={handleScatterZoomOut} disabled={scatterZoomLevel <= 2}>
                    <FaSearchPlus size={18} /> {/* Increased icon size */}
                  </ZoomButton>
                </ZoomContainer>
                <GridContainer>
                  <div style={{ width: '400px', height: '400px', margin: '20px auto' }}>
                    <Scatter data={scatterData} options={scatterOptions} plugins={[drawCirclesPlugin, donutMarkerPlugin]} />
                  </div>
                </GridContainer>

                {/* <StyledHeading>
                  <FaChartLine style={{ marginRight: '8px', fontSize: '24px' }} />
                  변위 결과
                </StyledHeading> */}
                <InfoRow>
                  <Label><FaCrosshairs size={18} style={{ marginRight: '5px' }} /> 기준 수신기:</Label>
                  <Value>
                    {baseSerial} 
                    {' ( '}
                    {deviationPoints.length > 0 ? deviationPoints[deviationPoints.length - 1].bQ || "N/A" : "N/A"}
                    {' )'}
                  </Value>
                </InfoRow>
                <InfoRow>
                  <Label><FaExchangeAlt size={18} style={{ marginRight: '5px' }} /> 비교 수신기:</Label>
                  <Value>
                    {compareSerial} 
                    {' ( '}
                    {deviationPoints.length > 0 ? deviationPoints[deviationPoints.length - 1].cQ || "N/A" : "N/A"}
                    {' )'}
                  </Value>
                </InfoRow>

                <InfoRow style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
                  <Label style={{ fontSize: '18px', fontWeight: 'bold', color: '#4A4A4A' }}>
                    RMS
                  </Label>
                </InfoRow>

                <InfoRow style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
                  <Label style={{ display: 'flex', alignItems: 'center', fontSize: '18px', color: '#333' }}>
                    <FaRulerHorizontal size={20} style={{ marginRight: '8px', color: '#007bff' }} />
                    Horizontal:
                  </Label>
                  <Value style={{ marginLeft: 'auto', fontSize: '20px', fontWeight: 'bold', color: '#007bff' }}>
                    {displacement.H ? `${(parseFloat(displacement.H) * 100).toFixed(1)} cm` : 'N/A'}
                  </Value>
                </InfoRow>

                <InfoRow style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
                  <Label style={{ display: 'flex', alignItems: 'center', fontSize: '16px', color: '#333' }}>
                    <FaRulerVertical size={20} style={{ marginRight: '8px', color: '#28a745' }} />
                    Vertical:
                  </Label>
                  <Value style={{ marginLeft: 'auto', fontSize: '20px', fontWeight: 'bold', color: '#28a745' }}>
                    {displacement.V ? `${(parseFloat(displacement.V) * 100).toFixed(1)} cm` : 'N/A'}
                  </Value>
                </InfoRow>

                <InfoRow>
                  <Label><FaClock size={18} style={{ marginRight: '5px' }} /> 측정 시간:</Label> {/* Increased icon size */}
                  <Value>{date(displacement.createdAt)}</Value>
                </InfoRow>
              </DisplacementInfo>
            )}
          </ComparisonPanel>
        </>
      )}
      <ButtonWrapper>
        <div style={{ display: "flex" }}>
          {/* Marker Button */}
          <KeywordButton onClick={handleOpenKeywordModal}>
            <FaMapMarkerAlt style={{ marginRight: '5px' }} />
            Marker
          </KeywordButton>

          {/* Refresh Button */}
          <RefreshButton onClick={handleRefresh}>
            <FaSyncAlt style={{ marginRight: '5px' }} />
            Refresh
          </RefreshButton>
        </div>
        <div>
          {/* Path Button */}
          <SketchButton onClick={handleOpenPolygonModal}>
            <FaRoute style={{ marginRight: '5px' }} />
            Path
          </SketchButton>

          {/* Path List Button */}
          <SketchButton onClick={handleOpenPolygonListModal}>
            <FaListAlt style={{ marginRight: '5px' }} />
            Path List
          </SketchButton>
        </div>
      </ButtonWrapper>
       <KeywordExplanationModal isOpen={isKeywordModalOpen} onClose={handleCloseKeywordModal} />

      <PolygonModal
        isOpen={isPolygonModalOpen && !isModalTemporarilyClosed}
        onClose={handleClosePolygonModal}
        onSave={handlePolygonSave}
        mouseCoordinates={mouseCoordinates} // Pass current coordinates
        onGetLocation={handleGetLocation} // Capture location from the map
        offGetLocation={handleOffGetLocation}
      />
      <PolygonListModal isOpen={isPolygonListModalOpen && !isModalTemporarilyClosed} onClose={handleClosePolygonListModal} mouseCoordinates={mouseCoordinates2} onGetLocation={handleGetLocation} offGetLocation={handleOffGetLocation} polygonCoordsDB={polygonCoordsDB} setPolygonCoordsDB={setPolygonCoordsDB} />
    </div>
  );
}

const MarkerTitle = styled.div`
  background-color: ${(props) => (props.isActive ? "#1364c7" : "#b7e8fd")};
  font-weight: bold;
  padding: 5px 5px;
  border-radius: 5px;
  transform: translate(-50%, 10%);
  color: ${(props) => (props.isActive ? "white" : "black")};
  font-size: 13px;
  z-index: ${(props) => (props.isActive ? 10 : 0)};
  position: relative;
  width: max-content;

  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const SketchButton = styled.button`
  background-color: navy;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  font-size: 16px;
  margin-top: 15px;
  margin-left: 5px;
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.3s ease, transform 0.2s ease;

  &:nth-child(2) {
    background-color: #007bff;
  }

  &:hover {
    opacity: 1.0;
  }

  &:active {
    transform: scale(0.98);
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 768px) {
    padding: 8px 15px;
    margin-top: 0;
    font-size: 14px;
  }

  @media (max-width: 480px) {
    padding: 6px 10px;
    font-size: 12px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 10px 0;

  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
    padding: 0 5px;
  }
`;

const KeywordButton = styled.button`
  background-color: transparent;
  color: #28a745;
  border: 2px solid #28a745;
  padding: 8px 12px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-right: 10px;

  &:hover {
    background-color: #28a745;
    color: white;
  }

  &:focus {
    outline: none;
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    font-size: 12px;
    padding: 6px 10px;
  }
`;

const KeywordModalContent = styled.div`
  padding: 20px;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  max-width: 600px;
  height: fit-content;
  margin: 0 auto;
  position: relative;

  @media (max-width: 768px) {
    max-width: 90%;
  }

  @media (max-width: 480px) {
    padding: 15px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: transparent;
  border: none;
  font-size: 18px;
  cursor: pointer;

  &:hover {
    color: red;
  }
`;

const KeywordTitle = styled.h2`
  font-size: 20px;
  margin-bottom: 20px;
  text-align: center;
  color: #007bff;

  @media (max-width: 768px) {
    font-size: 18px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const MarkerExplanation = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const MarkerImage = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;

  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
  }

  @media (max-width: 480px) {
    width: 30px;
    height: 30px;
  }
`;

const KeywordLabel = styled.span`
  font-size: 16px;
  font-weight: bold;
  color: #333;

  @media (max-width: 768px) {
    font-size: 14px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const ExplanationText = styled.p`
  font-size: 14px;
  color: #666;
  text-align: center;
  margin-top: 20px;

  @media (max-width: 768px) {
    font-size: 13px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const MarkerSet = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const MarkerInfo = styled.div`
  text-align: center;
  font-size: 14px;
  font-weight: bold;
  color: #666;

  @media (max-width: 768px) {
    font-size: 12px;
  }

  @media (max-width: 480px) {
    font-size: 10px;
  }
`;

const MarkerDescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  @media (max-width: 768px) {
    align-items: flex-start;
  }
`;

const RefreshButton = styled.button`
  background-color: #28a745;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #218838;
  }

  &:focus {
    outline: none;
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    padding: 6px 10px;
    font-size: 12px;
  }

  @media (max-width: 480px) {
    padding: 5px 8px;
  }
`;

const RadiusLabel = styled.div`
  background-color: rgba(255, 255, 255, 0.8);
  color: rgba(0, 0, 0, 0.85);
  padding: 6px 12px;
  border-radius: 10px;

  font-size: ${(props) => {
    const baseFontSize = Math.max(10, props.zoom * 1.2);
    if (props.zoom >= 10 && props.zoom <= 20) {
      return `${baseFontSize / 1.5}px`;
    }
    return `${baseFontSize}px`;
  }};
  
  font-weight: 500;
  max-width: 100px;
  text-align: center;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  transform: translate(-50%, -120%);
  position: absolute;

  @media (max-width: 768px) {
    font-size: ${(props) => {
      const baseFontSize = Math.max(8, props.zoom * 1.1);
      return `${baseFontSize}px`;
    }};
  }
`;

const ComparisonPanel = styled.div`
  position: absolute;
  top: 20px;
  right: 30px;
  background: ${(props) => (props.isActive ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0)')};
  padding: 15px;
  border-radius: 10px;
  box-shadow: ${(props) => (props.isActive ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none')};
  width: fit-content;
  z-index: 10;
  cursor: grab;
  transition: background 0.5s ease, box-shadow 0.5s ease, transform 0.3s ease-in-out;

  &:hover {
    box-shadow: ${(props) => (props.isActive ? '0 8px 16px rgba(0, 0, 0, 0.2)' : 'none')};
    transform: ${(props) => (props.isActive ? 'translateY(-2px)' : 'none')};
  }

  &:active {
    cursor: grabbing;
  }

  @media (max-width: 768px) {
    top: 3px;
    right: 80px;
    padding: 8px;
  }

  @media (max-width: 480px) {
    top: 3px;
    right: 80px;
    padding: 8px;
  }
`;

const MinimizeButton = styled.button`
  background-color: transparent;
  color: #1e5b9e;
  border: none;
  cursor: pointer;
  font-size: 18px;
  margin-left: 10px;
  transition: color 0.3s ease;

  &:hover {
    color: #174874;
  }

  &:focus {
    outline: none;
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ComparisonToggleButton = styled.button`
  background-color: #1e5b9e;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 16px;
  gap: 10px;
  margin-bottom: 15px;
  transition: all 0.3s ease;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  font-weight: 500;

  &:hover {
    background-color: #174874;
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    padding: 10px 15px;
    font-size: 14px;
  }

  @media (max-width: 480px) {
    padding: 8px 12px;
  }
`;

const ComparisonSettings = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 15px;

  label {
    font-size: 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 500;
  }

  select {
    margin-left: 8px;
    padding: 7px;
    border-radius: 6px;
    border: 1px solid #ccc;
    flex-grow: 1;
    transition: border-color 0.3s ease;

    &:focus {
      border-color: #1364c7;
    }
  }

  @media (max-width: 768px) {
    label {
      font-size: 12px;
    }

    select {
      font-size: 12px;
    }
  }
`;

const ComparisonButton = styled.button`
  background-color: ${(props) => (props.primary ? "#2e7bbd" : "#6baed6")};
  color: white;
  border: none;
  padding: 10px 25px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);

  &:hover {
    background-color: ${(props) => (props.primary ? "#236599" : "#508dbf")};
  }

  &:active {
    transform: scale(0.98);
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 768px) {
    font-size: 12px;
    padding: 8px 20px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
    padding: 6px 15px;
  }
`;

const ButtonWrapperDisplacementInfo = styled.div`
  display: flex;
  justify-content: space-around;
  gap: 10px;
  margin-top: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 5px;
  }
`;

const DisplacementInfo = styled.div`
  font-size: 13px;
  color: #333;
  background-color: #f0f4f8;
  border-radius: 10px;
  box-shadow: ${(props) => (props.isActive ? '0 3px 10px rgba(0, 0, 0, 0.1)' : 'none')};
  line-height: 1.6;
  font-weight: 500;
  overflow: hidden;

  max-height: ${(props) => (props.isActive ? '1000px' : '0px')};
  opacity: ${(props) => (props.isActive ? '1' : '0')};
  padding: ${(props) => (props.isActive ? '12px' : '0 12px')};
  transition: max-height 0.8s ease-in-out, opacity 0.8s ease-in-out, padding 0.8s ease-in-out, box-shadow 0.8s ease-in-out;

  h3 {
    font-size: 16px;
    color: #1364c7;
    text-align: center;
    opacity: ${(props) => (props.isActive ? '1' : '0')};
    transition: opacity 0.6s ease-in-out;
  }

  @media (max-width: 768px) {
    font-size: 12px;
  }

  @media (max-width: 480px) {
    font-size: 10px;
  }
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 1.5em;

  @media (max-width: 768px) {
    font-size: 1.2em;
  }

  @media (max-width: 480px) {
    font-size: 1em;
  }
`;

const Label = styled.span`
  font-weight: bold;
  color: #555;
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const Value = styled.span`
  color: #333;
  font-weight: bold;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const WarningMessage = styled.div`
  background-color: rgba(255, 0, 0, 0.1);
  color: #ff4d4f;
  padding: 8px;
  border-radius: 8px;
  font-size: 12px;
  margin-top: 10px;
  text-align: center;
  border: 1px solid rgba(255, 0, 0, 0.2);
  white-space: pre-wrap;

  @media (max-width: 768px) {
    font-size: 10px;
  }

  @media (max-width: 480px) {
    font-size: 9px;
  }
`;

const ZoomContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 5px;
  margin: 10px 0 20px 0;

  @media (max-width: 768px) {
    gap: 3px;
  }

  @media (max-width: 480px) {
    margin: 5px 0 10px 0;
  }
`;

const ZoomButton = styled.button`
  background-color: #007bff;
  border: none;
  color: white;
  padding: 6px;
  border-radius: 50%;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #0056b3;
  }

  &:focus {
    outline: none;
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    padding: 5px;
  }

  @media (max-width: 480px) {
    padding: 4px;
  }
`;

const GridContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 15px;

  @media (max-width: 768px) {
    padding: 8px;
  }

  @media (max-width: 480px) {
    padding: 5px;
  }
`;

const ComparisonHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const StyledHeading = styled.h3`
  font-size: 24px;
  font-weight: bold;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 15px;
  color: #333;

  @media (max-width: 768px) {
    font-size: 20px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const CursorLabel = styled.div`
  position: absolute;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 6px 10px;
  border-radius: 5px;
  font-size: 14px;
  pointer-events: none;
  z-index: 9999;

  @media (max-width: 768px) {
    font-size: 12px;
    padding: 5px;
  }

  @media (max-width: 480px) {
    font-size: 10px;
    padding: 4px;
  }
`;

export default memo(MonitoringMap);
